import React, { useEffect, useState } from 'react';
import { enterpriseEventsService, MigrationPhase, RecommendationStatus } from '../../services/enterpriseEventsService';
import { PROGRAM6_DOMAINS, program6Service } from '../../services/program6Service';
import { program7Service } from '../../services/program7Service';

type Props = {
  screenId: string;
  onActionComplete: () => void;
  migrationPayload?: Record<string, unknown> | null;
};

const MIGRATION_PHASES: MigrationPhase[] = ['legacy', 'shadow', 'dual_write', 'event_primary', 'complete'];

function btn(primary: string) {
  return `rounded-lg px-3 py-2 text-sm text-white disabled:opacity-50 ${primary}`;
}

export const EnterpriseModernizationActions: React.FC<Props> = ({ screenId, onActionComplete, migrationPayload }) => {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [domainKey, setDomainKey] = useState('statistics');
  const [domainPhase, setDomainPhase] = useState<MigrationPhase>('shadow');
  const [recommendationKey, setRecommendationKey] = useState('contacts_index_dedup');
  const [recStatus, setRecStatus] = useState<RecommendationStatus>('approved');
  const [workflowHint, setWorkflowHint] = useState<string | null>(null);

  const activeDomain =
    typeof migrationPayload?.active_domain === 'string' && migrationPayload.active_domain !== 'all_certified'
      ? migrationPayload.active_domain
      : domainKey;

  useEffect(() => {
    if (activeDomain && activeDomain !== 'all_certified') {
      setDomainKey(activeDomain);
    }
  }, [activeDomain]);

  useEffect(() => {
    if (!screenId.startsWith('program6')) return;
    void program6Service.getWorkflow(activeDomain).then((resp) => {
      const data = resp.data as Record<string, unknown> | undefined;
      if (data?.message) setWorkflowHint(String(data.message));
    });
  }, [screenId, activeDomain, message]);

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await fn();
      setMessage(`${label}: ${JSON.stringify(result)}`);
      onActionComplete();
    } catch (e: unknown) {
      setMessage(e instanceof Error ? e.message : `${label} failed`);
    } finally {
      setBusy(false);
    }
  };

  const domainActions = (
    <>
      <label className="text-xs text-indigo-800">
        Domain
        <select
          value={domainKey}
          onChange={(e) => setDomainKey(e.target.value)}
          className="mt-1 block rounded border border-indigo-200 px-2 py-1 text-sm"
        >
          {PROGRAM6_DOMAINS.map((d) => (
            <option key={d} value={d}>
              {d}
              {d === activeDomain ? ' (active)' : ''}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        disabled={busy}
        onClick={() => void run(`Capture baseline (${domainKey})`, () => program6Service.captureBaseline(domainKey))}
        className={btn('bg-slate-600 hover:bg-slate-700')}
      >
        Capture baseline
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => void run(`Advance ${domainKey}`, () => program6Service.advanceMigration(domainKey))}
        className={btn('bg-indigo-600 hover:bg-indigo-700')}
      >
        Advance phase
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => void run(`Verify ${domainKey}`, () => program6Service.verifyMigration(domainKey))}
        className={btn('bg-indigo-600 hover:bg-indigo-700')}
      >
        Verify shadow/dual-write
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => void run(`Compare ${domainKey}`, () => program6Service.comparePerformance(domainKey))}
        className={btn('bg-teal-600 hover:bg-teal-700')}
      >
        Performance compare
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => void run(`Rollback ${domainKey}`, () => program6Service.rollbackMigration(domainKey))}
        className={btn('bg-slate-600 hover:bg-slate-700')}
      >
        Rollback
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => void run(`Certify ${domainKey}`, () => program6Service.certifyDomain(domainKey))}
        className={btn('bg-violet-700 hover:bg-violet-800')}
      >
        Certify domain
      </button>
    </>
  );

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 shadow-sm">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-indigo-800">Modernization actions</h3>
      <p className="mt-1 text-xs text-indigo-700">Flag-gated operations — enable PROGRAM6_* env vars in staging first.</p>
      {workflowHint && screenId.startsWith('program6') && (
        <p className="mt-2 rounded bg-white px-2 py-1 text-xs text-indigo-900">Next: {workflowHint}</p>
      )}

      <div className="mt-3 flex flex-wrap items-end gap-2">
        {screenId === 'event-platform' && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void run('Health ping', () => enterpriseEventsService.publishHealthPing())}
            className={btn('bg-indigo-600 hover:bg-indigo-700')}
          >
            Publish health ping
          </button>
        )}

        {screenId === 'outbox-framework' && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void run('Relay outbox', () => enterpriseEventsService.relayOutbox())}
            className={btn('bg-indigo-600 hover:bg-indigo-700')}
          >
            Relay outbox batch
          </button>
        )}

        {screenId === 'read-model-projectors' && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void run('Run projectors', () => enterpriseEventsService.runProjectors())}
            className={btn('bg-indigo-600 hover:bg-indigo-700')}
          >
            Run projector batch
          </button>
        )}

        {screenId === 'deployment-drain' && (
          <>
            <button type="button" disabled={busy} onClick={() => void run('Start drain', () => enterpriseEventsService.startDrain())} className={btn('bg-amber-600 hover:bg-amber-700')}>
              Start drain
            </button>
            <button type="button" disabled={busy} onClick={() => void run('Complete drain', () => enterpriseEventsService.completeDrain())} className={btn('bg-emerald-600 hover:bg-emerald-700')}>
              Complete drain
            </button>
            <button type="button" disabled={busy} onClick={() => void run('Cancel drain', () => enterpriseEventsService.cancelDrain())} className={btn('bg-slate-600 hover:bg-slate-700')}>
              Cancel drain
            </button>
          </>
        )}

        {(screenId === 'program6-migrations' || screenId === 'program6-performance') && domainActions}

        {screenId === 'program7-deployment' && (
          <>
            <button type="button" disabled={busy} onClick={() => void run('Start rolling deploy', () => program7Service.startDeployment('rolling'))} className={btn('bg-amber-600 hover:bg-amber-700')}>
              Start rolling deployment
            </button>
            <button type="button" disabled={busy} onClick={() => void run('Validate SLOs', () => program7Service.validateSlos())} className={btn('bg-indigo-600 hover:bg-indigo-700')}>
              Validate SLOs
            </button>
          </>
        )}

        {screenId === 'program7-eoat' && (
          <>
            <button type="button" disabled={busy} onClick={() => void run('EOAT load suite', () => program7Service.runEoatSuite('load'))} className={btn('bg-indigo-600 hover:bg-indigo-700')}>
              Run load suite
            </button>
            <button type="button" disabled={busy} onClick={() => void run('EOAT failure suite', () => program7Service.runEoatSuite('failure'))} className={btn('bg-indigo-600 hover:bg-indigo-700')}>
              Run failure suite
            </button>
            <button type="button" disabled={busy} onClick={() => void run('EOAT full suite', () => program7Service.runEoatSuite('full'))} className={btn('bg-violet-700 hover:bg-violet-800')}>
              Run full EOAT
            </button>
          </>
        )}

        {screenId === 'program7-certification' && (
          <>
            <button type="button" disabled={busy} onClick={() => void run('Generate full report', () => program7Service.generateReport('full'))} className={btn('bg-violet-700 hover:bg-violet-800')}>
              Generate full certification report
            </button>
            <button type="button" disabled={busy} onClick={() => void run('Enterprise readiness', () => program7Service.generateReport('enterprise_readiness'))} className={btn('bg-indigo-600 hover:bg-indigo-700')}>
              Enterprise readiness report
            </button>
          </>
        )}

        {screenId === 'program7-readiness' && (
          <button type="button" disabled={busy} onClick={() => void run('Refresh readiness', () => program7Service.getReadinessReport())} className={btn('bg-emerald-600 hover:bg-emerald-700')}>
            Refresh readiness report
          </button>
        )}

        {screenId === 'program7-dashboard' && (
          <button type="button" disabled={busy} onClick={() => void run('Program 7 status', () => program7Service.getStatus())} className={btn('bg-indigo-600 hover:bg-indigo-700')}>
            Refresh Program 7 status
          </button>
        )}

        {screenId === 'program6-physical-optimization' && (
          <>
            <button type="button" disabled={busy} onClick={() => void run('Dry-run optimization', () => program6Service.runPhysicalOptimization(true))} className={btn('bg-indigo-600 hover:bg-indigo-700')}>
              Dry-run post-cert optimization
            </button>
            <button type="button" disabled={busy} onClick={() => void run('Apply optimization', () => program6Service.runPhysicalOptimization(false))} className={btn('bg-emerald-600 hover:bg-emerald-700')}>
              Apply approved rollouts
            </button>
          </>
        )}

        {screenId === 'program6-certification' && (
          <>
            <button type="button" disabled={busy} onClick={() => void run('Load cert', () => program6Service.runLoadCertification())} className={btn('bg-indigo-600 hover:bg-indigo-700')}>
              Run load certification
            </button>
            <button type="button" disabled={busy} onClick={() => void run('Resilience cert', () => program6Service.runResilienceCertification())} className={btn('bg-indigo-600 hover:bg-indigo-700')}>
              Run resilience certification
            </button>
            <button type="button" disabled={busy} onClick={() => void run('Deployment cert', () => program6Service.runDeploymentCertification())} className={btn('bg-indigo-600 hover:bg-indigo-700')}>
              Run deployment certification
            </button>
            <button type="button" disabled={busy} onClick={() => void run('Full certification', () => program6Service.runFullCertification())} className={btn('bg-violet-700 hover:bg-violet-800')}>
              Run full certification
            </button>
          </>
        )}
      </div>

      {screenId === 'domain-migration' && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="text-xs text-indigo-800">
            Domain
            <input value={domainKey} onChange={(e) => setDomainKey(e.target.value)} className="mt-1 block rounded border border-indigo-200 px-2 py-1 text-sm" />
          </label>
          <label className="text-xs text-indigo-800">
            Phase
            <select value={domainPhase} onChange={(e) => setDomainPhase(e.target.value as MigrationPhase)} className="mt-1 block rounded border border-indigo-200 px-2 py-1 text-sm">
              {MIGRATION_PHASES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={busy}
            onClick={() => void run('Update domain phase', () => enterpriseEventsService.updateDomainPhase(domainKey, domainPhase))}
            className={btn('bg-indigo-600 hover:bg-indigo-700')}
          >
            Advance phase
          </button>
        </div>
      )}

      {screenId === 'db-optimization-registry' && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="text-xs text-indigo-800">
            Recommendation key
            <input value={recommendationKey} onChange={(e) => setRecommendationKey(e.target.value)} className="mt-1 block rounded border border-indigo-200 px-2 py-1 text-sm" />
          </label>
          <label className="text-xs text-indigo-800">
            Status
            <select value={recStatus} onChange={(e) => setRecStatus(e.target.value as RecommendationStatus)} className="mt-1 block rounded border border-indigo-200 px-2 py-1 text-sm">
              {(['pending', 'approved', 'rejected', 'rolled_back'] as RecommendationStatus[]).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={busy}
            onClick={() => void run('Update recommendation', () => enterpriseEventsService.updateRecommendationStatus(recommendationKey, recStatus))}
            className={btn('bg-indigo-600 hover:bg-indigo-700')}
          >
            Update status
          </button>
        </div>
      )}

      {message && <pre className="mt-3 max-h-40 overflow-auto rounded bg-white p-2 text-xs text-indigo-900">{message}</pre>}
    </div>
  );
};
